"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TheArtOfFragranceHero() {
  const headlineWords = [{
    text: "The",
    italic: false
  }, {
    text: "Essence",
    italic: false
  }, {
    text: "of",
    italic: false
  }, {
    text: "Pure",
    italic: false
  }, {
    text: "Luxury",
    italic: true
  }];
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.1
  });
  const {
    ref: labelRef,
    isVisible: labelVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: bodyRef,
    isVisible: bodyVisible
  } = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.1
  });
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  return <section data-section-id="4207" id="the-art-of-fragrance-hero" className="relative bg-background text-foreground overflow-hidden min-h-screen flex items-center">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.04] mix-blend-multiply" style={{
      backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")"
    }} aria-hidden="true" />

      <div className="relative w-full max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-20 lg:py-0">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {}
          <div className="lg:col-span-7 order-2 lg:order-1 relative">
            {}
            <div ref={labelRef} className={`font-mono text-[11px] md:text-xs tracking-[0.25em] uppercase text-muted-foreground mb-8 lg:mb-12 transition-all duration-700 ease-out ${labelVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
              <span className="inline-block align-middle w-8 h-px bg-muted-foreground mr-3" />
              EST. 2024 / COLLECTION NO. 01
            </div>

            {}
            <h1 ref={headlineRef} className="font-serif font-default font-light text-foreground text-[64px] sm:text-[88px] md:text-[120px] lg:text-[140px] xl:text-[160px] leading-[0.95] tracking-tight mb-10 lg:mb-12" style={{
            letterSpacing: "-0.03em"
          }}>
              {headlineWords.map((word, i) => <span key={i} data-index={i} className="inline-block overflow-hidden align-bottom mr-[0.2em]">
                  <span className={`inline-block transition-all duration-700 ease-out ${headlineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"} ${word.italic ? "italic font-medium" : ""}`} style={{
                transitionDelay: headlineVisible ? `${i * 60}ms` : "0ms"
              }}>
                    {word.text}
                  </span>
                </span>)}
            </h1>

            {}
            <p ref={bodyRef} className={`font-serif font-default text-[17px] md:text-[19px] leading-relaxed text-muted-foreground max-w-[420px] mb-10 transition-all duration-700 ease-out delay-300 ${bodyVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
              Discover a curated collection of artisanal fragrances. 
              Each scent is a delicate balance of rare botanicals, 
              crafted to evoke timeless elegance and personal mystery.
            </p>

            {}
            <div ref={ctaRef} className={`flex flex-col sm:flex-row items-start sm:items-center gap-5 sm:gap-7 transition-all duration-700 ease-out delay-500 ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
              <Button asChild size="lg" className="rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground font-sans font-medium tracking-wide px-7 h-12 text-[14px] shadow-sm">
                <Link to="/ShopPage">Explore Our Scents</Link>
              </Button>

              <Link to="/OurHeritagePage" className="group inline-flex items-center gap-2 font-serif font-default text-foreground text-[15px] md:text-[16px] underline underline-offset-[6px] decoration-foreground/30 hover:decoration-foreground transition-all">
                Our Heritage
                <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>

            {}
            <div className="hidden lg:block absolute -bottom-6 left-0 text-chart-1/70" aria-hidden="true">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M24 4 L26 22 L44 24 L26 26 L24 44 L22 26 L4 24 L22 22 Z" stroke="currentColor" strokeWidth="1" strokeLinejoin="round" fill="none" />
                <circle cx="24" cy="24" r="2" fill="currentColor" />
              </svg>
            </div>
          </div>

          {}
          <div className="lg:col-span-5 order-1 lg:order-2 relative">
            <div ref={imageRef} className={`relative transition-all duration-1000 ease-out ${imageVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              <div className="relative overflow-hidden shadow-2xl" style={{
              aspectRatio: "5 / 7",
              borderRadius: "0px"
            }}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701222/img/luxury_perfume_bottle_floral.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701222/img/luxury_perfume_bottle_floral.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701222/img/luxury_perfume_bottle_floral.png-wvc-sizes" alt="Elegant perfume bottle surrounded by delicate floral petals and soft mist" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                {}
                <div className="pointer-events-none absolute inset-0 opacity-[0.08] mix-blend-overlay" style={{
                backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n2'><feTurbulence type='fractalNoise' baseFrequency='1.2' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n2)'/></svg>\")"
              }} aria-hidden="true" />
                {}
                <div className="pointer-events-none absolute inset-0" style={{
                background: "linear-gradient(180deg, hsla(28, 18%, 14%, 0) 60%, hsla(28, 18%, 14%, 0.15) 100%)"
              }} aria-hidden="true" />
              </div>

              {}
              <div className="absolute -left-3 lg:-left-6 bottom-10 bg-background/90 backdrop-blur-sm px-4 py-2 rounded-lg shadow-sm border border-border">
                <span className="font-mono text-[10px] md:text-[11px] tracking-[0.2em] uppercase text-muted-foreground">
                  Signature / No. 001
                </span>
              </div>
            </div>
          </div>
        </div>

        {}
        <div className="hidden lg:flex absolute right-8 bottom-8 items-center gap-3 select-none">
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground" style={{
          writingMode: "vertical-rl",
          transform: "rotate(180deg)"
        }}>
            scroll
          </span>
          <span className="w-px h-16 bg-muted-foreground/40 relative overflow-hidden">
            <span className="absolute top-0 left-0 w-full h-6 bg-foreground/70 animate-[scrollLine_2s_ease-in-out_infinite]" />
          </span>
        </div>
      </div>

      <style>{`
        @keyframes scrollLine {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(400%); }
        }
      `}</style>
    </section>;
}